/**
 * InsightVariableReviewPanel — Accept/reject generated variables with explanations
 *
 * R6 (strategy-flow eval-debug program, design gate 2026-07-31): the review
 * surface uses the canonical bulk-selection pattern (as in strategies):
 * SDataTable with always-on row checkboxes + SelectionActionsBar with the
 * "Apply N Variables" bulk action. Selection state IS the persisted
 * `accepted` flag — the table's rowSelection mirrors Firestore and every
 * selection change is diffed and written back. Per-variable depth
 * (options/affinity, AI reasoning, data usage) lives in the row detail panel.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Stack, Box, Typography, Button, Chip,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import { DataMappingViewerV2 } from '@modules/strategy-blocks/components/DataMappingViewerV2/DataMappingViewerV2';
import {
  SDataTable,
  SelectionActionsBar,
  useSelectionMode,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import {
  InsightVariableGenerationService,
  type InsightVariableGenerationData,
  type GeneratedVariableEntityData,
} from '@modules/firebase-core/services/InsightVariableGenerationService';
import { AgentApiClient } from '@modules/content-agent';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

// ── Props ────────────────────────────────────────────────────────────

interface InsightVariableReviewPanelProps {
  projectId: string;
  generationId: string;
  onApplied?: () => void;
}

interface VariableRow {
  id: string;
  name: string;
  type: string;
  screenType: string;
  optionsCount: number;
  entity: GeneratedVariableEntityData;
}

// ── Component ────────────────────────────────────────────────────────

export const InsightVariableReviewPanel: React.FC<InsightVariableReviewPanelProps> = ({
  projectId,
  generationId,
  onApplied,
}) => {
  const [generation, setGeneration] = useState<InsightVariableGenerationData | null>(null);
  const [variables, setVariables] = useState<GeneratedVariableEntityData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const service = useMemo(() => new InsightVariableGenerationService(projectId), [projectId]);

  // Selection = persisted `accepted`. The hook drives the table/bar UI;
  // Firestore stays the source of truth and re-syncs it on every snapshot.
  const selection = useSelectionMode({ defaultMode: true });
  const { setRowSelection } = selection;
  const acceptedMapRef = useRef<Record<string, boolean>>({});

  // ── Load data ─────────────────────────────────────────────────────
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    unsubs.push(service.subscribeToGeneration(generationId, setGeneration));
    unsubs.push(service.subscribeToVariables(generationId, (vars) => {
      setVariables(vars);
      setIsLoading(false);
      const accepted: Record<string, boolean> = {};
      for (const entity of vars) {
        if (entity.accepted === true) accepted[entity.id] = true;
      }
      acceptedMapRef.current = accepted;
      setRowSelection(accepted);
    }));

    return () => unsubs.forEach(u => u());
  }, [service, generationId, setRowSelection]);

  // ── Persist selection diffs ───────────────────────────────────────
  const handleRowSelectionChange = useCallback<React.Dispatch<React.SetStateAction<SDataTableRowSelectionState>>>(
    (updater) => {
      setRowSelection((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const updates: Array<{ variableId: string; accepted: boolean }> = [];
        const ids = new Set([...Object.keys(next), ...Object.keys(acceptedMapRef.current)]);
        for (const id of ids) {
          const selected = Boolean(next[id]);
          const persisted = Boolean(acceptedMapRef.current[id]);
          if (selected !== persisted) updates.push({ variableId: id, accepted: selected });
        }
        if (updates.length > 0) {
          for (const { variableId, accepted } of updates) {
            acceptedMapRef.current = { ...acceptedMapRef.current, [variableId]: accepted };
          }
          void service.bulkUpdateAccepted(generationId, updates).catch((err: unknown) => {
            setError(err instanceof Error ? err.message : 'Failed to save selection');
          });
        }
        return next;
      });
    },
    [service, generationId, setRowSelection],
  );

  // ── Apply ─────────────────────────────────────────────────────────
  const acceptedCount = selection.selectedIds.length;

  const handleApply = useCallback(async () => {
    if (acceptedCount === 0) {
      setError('No variables accepted. Accept at least one variable to apply.');
      return;
    }

    setIsApplying(true);
    try {
      const client = new AgentApiClient();
      const userId = CoreFirebaseService.getInstance().auth.currentUser?.uid || '';
      const result = await client.applyInsightVariables(generationId, { projectId, userId });

      if (!result.success) {
        setError(result.error || 'Failed to apply variables');
      } else {
        onApplied?.();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to apply variables');
    } finally {
      setIsApplying(false);
    }
  }, [acceptedCount, generationId, projectId, onApplied]);

  // ── Table shape ───────────────────────────────────────────────────
  const rows = useMemo<VariableRow[]>(() => variables.map((entity) => {
    const v = entity.data.variable as Record<string, any>;
    const explanation = entity.data.explanation as Record<string, any>;
    return {
      id: entity.id,
      name: String(v.name ?? entity.id),
      type: String(v.type ?? ''),
      screenType: String(explanation?.recommendedScreenType ?? ''),
      optionsCount: Array.isArray(v.options) ? v.options.length : 0,
      entity,
    };
  }), [variables]);

  const columns = useMemo<SDataTableColumnDef<VariableRow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Variable',
      Cell: ({ row }) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.original.name}</Typography>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 90,
      Cell: ({ row }) => (row.original.type
        ? <Chip label={row.original.type} size="small" variant="outlined" sx={{ height: 20 }} />
        : null),
    },
    {
      accessorKey: 'screenType',
      header: 'Screen',
      size: 130,
      Cell: ({ row }) => (row.original.screenType
        ? <Chip label={row.original.screenType} size="small" variant="outlined" color="primary" sx={{ height: 20 }} />
        : null),
    },
    { accessorKey: 'optionsCount', header: 'Options', size: 80 },
  ], []);

  // ── States ────────────────────────────────────────────────────────
  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (generation?.status === 'running') {
    return (
      <Alert severity="info">
        Generation in progress... Variables will appear here when complete.
      </Alert>
    );
  }

  if (generation?.status === 'error') {
    return (
      <Alert severity="error">
        Generation failed: {generation.error || 'Unknown error'}
      </Alert>
    );
  }

  if (variables.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No variables generated.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Overall strategy explanation */}
      {generation?.explanation?.overallStrategy && (
        <Alert severity="info" icon={false}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Strategy
          </Typography>
          <Typography variant="body2">
            {generation.explanation.overallStrategy}
          </Typography>
          {generation.explanation.confidenceEstimate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {generation.explanation.confidenceEstimate}
            </Typography>
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Canonical bulk bar: counter chip + Apply as the caller bulk action */}
      <SelectionActionsBar selection={selection} reserveSpace>
        <Button
          variant="contained"
          size="small"
          disabled={acceptedCount === 0 || generation?.status === 'applied' || isApplying}
          onClick={handleApply}
        >
          {isApplying
            ? 'Applying...'
            : generation?.status === 'applied'
            ? 'Applied'
            : `Apply ${acceptedCount} Variable${acceptedCount !== 1 ? 's' : ''}`}
        </Button>
      </SelectionActionsBar>

      <SDataTable<VariableRow>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        enableRowSelection
        state={{ rowSelection: selection.rowSelection }}
        onRowSelectionChange={handleRowSelectionChange}
        renderDetailPanel={({ row }) => renderVariableDetail(row.original.entity)}
      />
    </Stack>
  );
};

// ── Row detail: options/affinity + AI reasoning + data usage ─────────

function renderVariableDetail(entity: GeneratedVariableEntityData): React.ReactNode {
  const v = entity.data.variable as Record<string, any>;
  const explanation = entity.data.explanation as Record<string, any>;
  const optionMappings = entity.data.optionMappings as Array<Record<string, any>>;
  const dataUsage = (entity.data as any).dataUsage as Record<string, any> | undefined;

  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      {(v.options || []).length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {(v.options || []).map((opt: any) => {
            const mapping = optionMappings?.find((m: any) => m.optionId === opt.id);
            return (
              <Stack key={opt.id} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {opt.label}
                </Typography>
                {mapping?.actorAffinity && (
                  <Stack direction="row" spacing={0.5}>
                    {Object.entries(mapping.actorAffinity)
                      .filter(([, val]) => (val as number) > 0.3)
                      .map(([actorId, val]) => (
                        <Chip
                          key={actorId}
                          label={`${(val as number * 100).toFixed(0)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      ))}
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ mb: 1 }} />
      {explanation?.reasoning && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>Reasoning:</Typography>
          <Typography variant="body2" color="text.secondary">{explanation.reasoning}</Typography>
        </Box>
      )}
      {explanation?.splitMergeDecision && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>Split/Merge:</Typography>
          <Typography variant="body2" color="text.secondary">{explanation.splitMergeDecision}</Typography>
        </Box>
      )}
      {explanation?.identificationPower && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>Identification Power:</Typography>
          <Typography variant="body2" color="text.secondary">{explanation.identificationPower}</Typography>
        </Box>
      )}
      {explanation?.questionFraming && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>Question Framing:</Typography>
          <Typography variant="body2" color="text.secondary">{explanation.questionFraming}</Typography>
        </Box>
      )}
      {v.description && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight={600}>Full Description (for AI context):</Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
            {v.description}
          </Typography>
        </Box>
      )}
      {dataUsage && (
        <Box sx={{ mt: 1 }}>
          <DataMappingViewerV2
            dataUsage={dataUsage as any}
            defaultExpanded={false}
            title="Data Usage (AI Audit Trail)"
          />
        </Box>
      )}
    </Box>
  );
}
